import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

// Admin/superadmin-only view of rejected package_orders -- sales-reports.js
// deliberately excludes these from /reports entirely (KPIs, trend,
// breakdowns, transaction list), since they never represent a real
// sale. This is the dedicated place to actually see them.

function memberLabel(r) {
  const name = [r.first_name, r.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${r.wellis_id || '—'})`;
}

export default function RejectedSales() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');

  const [rejectedSales, setRejectedSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [authLoading, user, isAdmin, navigate]);

  const fetchRejectedSales = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/rejected-sales`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load rejected sales');
      setRejectedSales(data.rejected_sales || []);
    } catch (err) {
      setError(err.message || 'Failed to load rejected sales');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    fetchRejectedSales();
  }, [user, isAdmin, fetchRejectedSales]);

  if (!user || !isAdmin) return null;

  const cardStyle = { backgroundColor: '#0d2818', borderColor: '#c9a961' };
  const rowStyle = { backgroundColor: '#152214', borderColor: '#3d5a4a' };
  const searchLower = search.trim().toLowerCase();
  const filtered = rejectedSales.filter((r) => {
    if (!searchLower) return true;
    const haystack = `${r.wellis_id || ''} ${r.first_name || ''} ${r.last_name || ''}`.toLowerCase();
    return haystack.includes(searchLower);
  });
  const filteredTotal = filtered.reduce((sum, r) => sum + Number(r.amount || 0), 0);

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-headline-lg font-bold mb-1" style={{ color: '#c9a961' }}>
          {tt('Rejected Sales', '已拒绝销售')}
        </h1>
        <p className="text-body-sm mb-6" style={{ color: '#999999' }}>
          {tt(
            'Package orders that were rejected by an admin -- these never count toward Confirmed Sales, Pre-Processed Sales, or any other figure on /reports.',
            '被管理员拒绝的套餐订单——这些不计入销售报告中的"已确认销售额"、"预处理销售额"或任何其他数据。'
          )}
        </p>

        {error && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
            {error}
          </div>
        )}

        <input
          type="text"
          placeholder={tt('Search Wellis ID or name...', '搜索 Wellis ID 或姓名...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 mb-3 text-body-sm rounded border"
          style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#b8a68f' }}
        />

        <div className="flex items-center justify-between p-3 mb-4 rounded border" style={rowStyle}>
          <span className="text-body-sm" style={{ color: '#999999' }}>
            {tt('Total Rejected (shown below)', '已拒绝总额（下表所示）')}
          </span>
          <span className="text-headline-sm font-bold" style={{ color: '#c94961' }}>{fmt(filteredTotal)}</span>
        </div>

        {loading ? (
          <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
        ) : filtered.length === 0 ? (
          <div className="border rounded-lg p-6 text-center" style={cardStyle}>
            <p style={{ color: '#b8a68f' }}>{tt('No rejected sales.', '暂无已拒绝销售。')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
              <thead>
                <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                  <th className="p-2">{tt('Member', '会员')}</th>
                  <th className="p-2">{tt('Package', '套餐')}</th>
                  <th className="p-2">{tt('Amount', '金额')}</th>
                  <th className="p-2">{tt('Rejected By', '拒绝人')}</th>
                  <th className="p-2">{tt('Reason', '原因')}</th>
                  <th className="p-2">{tt('Rejected At', '拒绝时间')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                    <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(r)}</td>
                    <td className="p-2">{r.package_name || '—'}{r.package_code ? ` (${r.package_code})` : ''}</td>
                    <td className="p-2">{fmt(r.amount)}</td>
                    <td className="p-2">{r.rejected_by ? memberLabel({ ...r.rejected_by, wellis_id: r.rejected_by.affiliate_code }) : '—'}</td>
                    <td className="p-2" style={{ color: '#999999' }}>{r.admin_notes || '—'}</td>
                    <td className="p-2">{r.rejected_at ? new Date(r.rejected_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
